/**
 * One-click audit-ready review-session evidence bundle export.
 */
import React, { useState } from "react";
import { Button, Alert } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import {
  buildReviewSessionEvidenceBundle,
  isEvidenceBundleAuditReady,
  type ReviewSessionEvidenceBundle,
} from "../utils/clinicianOpsAndEvidence";

type ReviewSessionEvidenceExportButtonProps = {
  studyId: string;
  reviewerId: string;
  sessionId?: string;
  viewedRanges?: Array<{ startSeconds: number; endSeconds: number }>;
  qeegPanels?: string[];
  annotationIds?: string[];
  detectorCandidateIds?: string[];
  auditEventIds?: string[];
};

const ReviewSessionEvidenceExportButton: React.FC<
  ReviewSessionEvidenceExportButtonProps
> = ({
  studyId,
  reviewerId,
  sessionId,
  viewedRanges,
  qeegPanels,
  annotationIds,
  detectorCandidateIds,
  auditEventIds,
}) => {
  const { t } = useTranslation("features");
  const [bundle, setBundle] = useState<ReviewSessionEvidenceBundle | null>(
    null,
  );

  const onExport = () => {
    const next = buildReviewSessionEvidenceBundle({
      sessionId: sessionId || `session-${Date.now()}`,
      studyId,
      reviewerId,
      startedAt: new Date(Date.now() - 15 * 60_000),
      viewedRanges: viewedRanges || [{ startSeconds: 0, endSeconds: 60 }],
      qeegPanels: qeegPanels || [],
      annotationIds: annotationIds || [],
      detectorCandidateIds: detectorCandidateIds || [],
      auditEventIds: auditEventIds || [`audit-${Date.now()}`],
    });
    setBundle(next);
    const blob = new Blob([JSON.stringify(next, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `eeg-review-evidence-${studyId}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="review-session-evidence-export">
      <Button size="sm" variant="outline-primary" onClick={onExport}>
        <Trans ns="features" i18nKey="ui.copy.a1cea7b58b8c" />
      </Button>
      {bundle && (
        <Alert
          variant={isEvidenceBundleAuditReady(bundle) ? "success" : "warning"}
          className="mt-2 mb-0 py-2 small"
        >
          {isEvidenceBundleAuditReady(bundle)
            ? t("ui.copy.evidenceBundleAuditReady", {
                defaultValue: "Evidence bundle audit-ready ({{version}})",
                version: bundle.schemaVersion,
              })
            : t("ui.copy.evidenceBundleIncomplete", {
                defaultValue: "Evidence bundle incomplete ({{version}})",
                version: bundle.schemaVersion,
              })}
        </Alert>
      )}
    </div>
  );
};

export default ReviewSessionEvidenceExportButton;
